<template>
  <div id="app">
    <v-app>
      <template v-if="isPublicRoute">
        <v-main>
          <router-view :key="$route.path" />
        </v-main>
        <the-footer />
      </template>
      <template v-else>
        <v-main>
          <loader-iusa />
          <nav-bar />
          <breadcrumb-roles v-if="!$hasAnyRole(['CustomerPR', 'Customer'])" />
          <customer-menu />
          <v-alert type="info" :value="alert" class="my-2">
            {{ message }}
          </v-alert>

          <!-- Key force view update when going to a same route but diferent parameters ex: /user/123 -> user/689 -->
          <router-view :key="$route.path" />
          <right-nav-drawer />
        </v-main>

        <reload-without-cache-btn v-if="authenticated" />
        <modal-steps-after-login />
        <test-c-f-e-notice-alert />
        <schedule-event-modal v-if="Policies.SchedulePolicy.canList()" />
        <the-footer />
      </template>
    </v-app>
  </div>
</template>

<script>
  import { mapGetters, mapMutations, mapActions } from 'vuex';
  import Pusher from 'pusher-js';

  import { eventBus } from '@/main.js';
  import generalMixin from './mixins/general';
  import userInactivityTime from './mixins/userInactivityTime';
  import NavBar from '@/template/partials/NavBar.vue';
  import CustomerMenu from '@/template/partials/customerMenu/CustomerMenu.vue';
  import BreadcrumbRoles from './template/partials/life_line/BreadcrumbRoles.vue';
  // import PRNavBar from "@/template/partials/PRNavBar.vue";
  //import SideBar from "@/template/partials/SideBar.vue";
  import RightNavDrawer from '@/template/partials/RightNavDrawer.vue';
  import TheFooter from '@/template/partials/Footer.vue';
  import LoaderIusa from '@/components/LoaderIusa.vue';
  import ModalStepsAfterLogin from '@/template/partials/stepsAfterLogin/ModalStepsAfterLogin.vue';
  import ReloadWithoutCacheBtn from './components/ReloadWithoutCacheBtn.vue';
  import maintenance from './mixins/maintenance';
  import TestCFENoticeAlert from './components/modals/TestCFENoticeAlert.vue';
  import GeolocationEmployeeUser from './mixins/GeolocationEmployeeUser';
  import { SCHEDULE_STORAGE_KEY } from './core/constants/schedule/notification';
  import ScheduleEventModal from '@/views/schedule/components/event/EventModal.vue';
  import Policies from './core/policies';

  export default {
    metaInfo: {
      titleTemplate(titleChunk) {
        return titleChunk ? `${titleChunk} – IUSA en Línea` : 'IUSA en Línea';
      },
    },

    components: {
      NavBar,
      // PRNavBar,
      //SideBar,
      CustomerMenu,
      TheFooter,
      RightNavDrawer,
      BreadcrumbRoles,
      LoaderIusa,
      ModalStepsAfterLogin,
      ReloadWithoutCacheBtn,
      TestCFENoticeAlert,
      ScheduleEventModal,
    },
    mixins: [
      generalMixin,
      userInactivityTime,
      maintenance,
      GeolocationEmployeeUser,
    ],
    data: () => {
      return {
        Policies,
        isScheduleNotificationsInitialized: false,
      };
    },
    computed: {
      ...mapGetters({
        token: 'auth/token',
        message: 'auth/message',
        alert: 'auth/alert',
        user: 'auth/user',
        channel: 'auth/channel',
        authenticated: 'auth/authenticated',
        userCode: 'auth/userCode',
        hasSteps: 'modalStepsAfterLogin/hasSteps',
        showModal: 'modalStepsAfterLogin/showModal',
        shouldShowEventConfirmationModal:
          'scheduleNotification/shouldShowConfirmationModal',
        pendingConfirmationEvents:
          'scheduleNotification/pendingConfirmationEvents',
        dismissedEvents: 'scheduleNotification/dismissedEvents',
      }),
      isPublicRoute() {
        return !!this.$route.meta?.public;
      },
    },
    watch: {
      authenticated(newValue) {
        this.launchModalSteps(newValue);
      },
      showModal(newValue) {
        if (newValue && !this.authenticated) this.updateModalState(false);
      },
      shouldShowEventConfirmationModal(newValue, oldValue) {
        if (!this.authenticated || !this.isScheduleNotificationsInitialized)
          return;

        if (!oldValue && newValue) {
          this.loadScheduleEventConfirmationStep(
            this.pendingConfirmationEvents
          );
          this.updateModalState(true);
        }
      },
      channel: {
        handler(channel) {
          const userId = this.user?.id;
          this.loadDescendants({ userId, channel });
        },
      },
    },
    created() {
      this.launchModalSteps(this.authenticated);

      this.checkBillingModuleActive();
      this.checkInitialRouteAccess();

      this.$router.beforeEach((to, from, next) => {
        if (this.isBillingRouteDisabled(to.name)) {
          this.launchBillingStepModal();
          next({ name: 'Home' });
          return;
        }

        next();
      });

      // SCHEDULE
      if (Policies.SchedulePolicy.canList()) {
        this.scheduleEventConfirmationInterval = setInterval(() => {
          this.$store.commit('scheduleNotification/SET_NOW');
        }, 60 * 1000);
      }

      window.addEventListener('storage', this.onStorageUpdate);
    },
    beforeDestroy() {
      document.removeEventListener('mousemove', this.resetInactivityTimer);
      document.removeEventListener('keydown', this.resetInactivityTimer);

      clearTimeout(this.inactivityTimeout);

      // SCHEDULE
      clearInterval(this.scheduleEventConfirmationInterval);
      window.removeEventListener('storage', this.onStorageUpdate);
    },
    methods: {
      ...mapActions({
        signOutAction: 'auth/signOut',
        signInVel: 'auth/sigInVel',
        loadModalSteps: 'modalStepsAfterLogin/loadModalSteps',
        updateModalState: 'modalStepsAfterLogin/updateModalState',
        loadScheduleEventConfirmationStep:
          'modalStepsAfterLogin/loadScheduleEventConfirmationStep',
        loadDescendants: 'scheduleNotification/loadDescendants',
        loadTodayEvents: 'scheduleNotification/loadTodayEvents',
      }),
      ...mapMutations({
        setVisibleAlert: 'auth/SET_VISIBLE_ALERT',
        addNewNotification: 'auth/ADD_NEW_NOTIFICATION',
        addDiary: 'diary/ADD_DIARY',
        setDrawerState: 'diary/SET_DRAWER_STATE',
      }),
      subscribe() {
        if (this.user && this.user.id) {
          var pusher = new Pusher(this.$pusherAppKey, {
            cluster: 'us2',
            authEndpoint: this.$baseApiUrl + '/pusher/auth',
          });
          pusher.subscribe('private-users-' + this.user.id);

          pusher.bind(
            'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
            (data) => {
              this.playSound();

              if (data.type == 'App\\Notifications\\CustomerRequestAccepted') {
                if (data.event_type == 'accepted') {
                  this.$swal(
                    'Tu registro ha sido aceptado. Se te redigirá en 3 segundos.',
                    { type: 'loading' }
                  );
                  setTimeout(() => {
                    if (this.$route.name != 'Home') {
                      this.$router.push('/home').then(() => {
                        location.reload();
                      });
                    } else {
                      location.reload();
                    }
                  }, 5000);
                } else {
                  this.addNewNotification(data);
                }
              } else {
                this.notify('Nuevo evento registrado');
                let event = {
                  notifiable_type: 'App\\Models\\User',
                  notifiable_id: this.user.id,
                  data: data,
                };
                eventBus.$emit('openDrawer', event);
              }
            }
          );
        }
      },
      async launchModalSteps(authenticated) {
        await this.loadModalSteps();

        this.isScheduleNotificationsInitialized = true;

        if (this.hasSteps && authenticated) this.updateModalState(true);
        else this.updateModalState(false);
      },

      // SUPPORT MULTIPLE TABS FOR SCHEDULE EVENTS (SYNCS FETCH ON DATA MUTATION)
      onStorageUpdate(e) {
        if (e.key === SCHEDULE_STORAGE_KEY) {
          const scheduleRoutes = ['ScheduleCalendar', 'ScheduleMetrics'];
          const isOnScheduleRoute = scheduleRoutes.includes(this.$route.name);

          if (isOnScheduleRoute) {
            window.location.reload();
          } else {
            this.loadTodayEvents();
          }
        }
      },
    },
  };
</script>

<style>
  #app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    background: rgb(255, 255, 255);
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 1) 85%,
      rgba(139, 139, 139, 1) 100%
    );
  }
  html,
  body {
    max-width: 100% !important;
    overflow-x: hidden !important;
  }
  .d-image {
    width: 100%;
  }
</style>
